/**
 * IDG Meeting Analytics Dashboard
 * 
 * Analytics view showing meeting trends, speaker participation,
 * and discussion topics across all IDG meetings.
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  BarChart3, Clock, Users, MessageSquare, TrendingUp, TrendingDown,
  Calendar, Mic, FileText, Brain, Activity, PieChart, ArrowUpRight,
  ArrowDownRight, Minus, RefreshCw, Download, Filter
} from "lucide-react";
import api from "@/lib/api";

// Demo analytics data
const DEMO_ANALYTICS = {
  summary: {
    total_meetings: 47,
    total_duration_hours: 62.5,
    avg_duration_minutes: 79,
    total_participants: 156,
    avg_participants: 5.2,
    transcription_accuracy: 94.5,
    action_items_created: 234,
    action_items_completed: 198
  },
  trends: {
    meetings_change: 12.5,
    duration_change: -8.3,
    participation_change: 5.7,
    completion_rate_change: 15.2
  },
  speaker_participation: [
    { role: "RN", name: "Registered Nurse", count: 47, percentage: 100, avg_speaking_time: 18, color: "bg-blue-500" },
    { role: "MD", name: "Physician", count: 42, percentage: 89, avg_speaking_time: 12, color: "bg-violet-500" },
    { role: "SW", name: "Social Worker", count: 38, percentage: 81, avg_speaking_time: 14, color: "bg-pink-500" },
    { role: "Chaplain", name: "Chaplain", count: 35, percentage: 74, avg_speaking_time: 10, color: "bg-purple-500" },
    { role: "Aide", name: "Home Health Aide", count: 28, percentage: 60, avg_speaking_time: 6, color: "bg-orange-500" },
    { role: "Coordinator", name: "Care Coordinator", count: 22, percentage: 47, avg_speaking_time: 8, color: "bg-teal-500" }
  ],
  topics_discussed: [
    { topic: "Pain Management", count: 42, percentage: 89, trend: "up" },
    { topic: "Medication Changes", count: 38, percentage: 81, trend: "up" },
    { topic: "Family Support", count: 35, percentage: 74, trend: "stable" },
    { topic: "Goals of Care", count: 32, percentage: 68, trend: "up" },
    { topic: "Symptom Control", count: 30, percentage: 64, trend: "stable" },
    { topic: "Spiritual Needs", count: 28, percentage: 60, trend: "down" },
    { topic: "Care Plan Updates", count: 26, percentage: 55, trend: "up" },
    { topic: "Equipment/DME", count: 18, percentage: 38, trend: "stable" }
  ],
  meetings_by_day: [
    { day: "Monday", count: 12, percentage: 26 },
    { day: "Tuesday", count: 8, percentage: 17 },
    { day: "Wednesday", count: 15, percentage: 32 },
    { day: "Thursday", count: 7, percentage: 15 },
    { day: "Friday", count: 5, percentage: 10 }
  ],
  recent_meetings: [
    { id: "m1", date: "2026-02-03", duration: 85, participants: 6, patients_discussed: 4, action_items: 8 },
    { id: "m2", date: "2026-02-01", duration: 72, participants: 5, patients_discussed: 3, action_items: 5 },
    { id: "m3", date: "2026-01-29", duration: 90, participants: 7, patients_discussed: 5, action_items: 12 },
    { id: "m4", date: "2026-01-27", duration: 65, participants: 4, patients_discussed: 3, action_items: 4 },
    { id: "m5", date: "2026-01-24", duration: 78, participants: 6, patients_discussed: 4, action_items: 7 }
  ],
  patients_most_discussed: [
    { name: "John Smith", discussions: 8, last_discussed: "2026-02-03", status: "Active" },
    { name: "Mary Johnson", discussions: 6, last_discussed: "2026-02-01", status: "Active" },
    { name: "Robert Williams", discussions: 5, last_discussed: "2026-01-29", status: "Active" },
    { name: "Patricia Davis", discussions: 4, last_discussed: "2026-01-27", status: "Discharged" }
  ]
};

export default function IDGMeetingAnalytics({ onClose }) {
  const [analytics, setAnalytics] = useState(DEMO_ANALYTICS);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState("30d");
  const [activeTab, setActiveTab] = useState("overview");

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/ehr/idg-dictation/analytics?time_range=${timeRange}`);
      if (response.data) {
        setAnalytics(response.data);
      }
    } catch (err) {
      // Use demo data
      console.log("Using demo analytics data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  const getTrendIcon = (change) => {
    if (change > 0) return <ArrowUpRight className="w-4 h-4 text-emerald-400" />;
    if (change < 0) return <ArrowDownRight className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getTrendColor = (change) => {
    if (change > 0) return "text-emerald-400";
    if (change < 0) return "text-red-400";
    return "text-slate-400";
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const exportAnalytics = () => {
    const content = `# IDG Meeting Analytics Report
Generated: ${new Date().toLocaleDateString()}
Time Range: ${timeRange === "7d" ? "Last 7 Days" : timeRange === "30d" ? "Last 30 Days" : "Last 90 Days"}

## Summary
- Total Meetings: ${analytics.summary.total_meetings}
- Total Duration: ${analytics.summary.total_duration_hours} hours
- Average Duration: ${analytics.summary.avg_duration_minutes} minutes
- Total Participants: ${analytics.summary.total_participants}
- Average Participants: ${analytics.summary.avg_participants}
- Transcription Accuracy: ${analytics.summary.transcription_accuracy}%
- Action Items Created: ${analytics.summary.action_items_created}
- Action Items Completed: ${analytics.summary.action_items_completed}

## Speaker Participation
${analytics.speaker_participation.map(s => `- ${s.name}: ${s.percentage}% attendance, avg ${s.avg_speaking_time} min speaking`).join('\n')}

## Top Discussion Topics
${analytics.topics_discussed.map(t => `- ${t.topic}: ${t.count} discussions (${t.percentage}%)`).join('\n')}
`;
    
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `idg-analytics-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Analytics report exported");
  };

  return (
    <div className="space-y-4" data-testid="idg-meeting-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-teal-400" />
            IDG Meeting Analytics
          </h2>
          <p className="text-slate-400 text-sm">Voice meeting trends and participation insights</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32 bg-slate-800 border-slate-600">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAnalytics} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportAnalytics}>
            <Download className="w-4 h-4 mr-1" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Meetings</p>
                <p className="text-2xl font-bold text-white">{analytics.summary.total_meetings}</p>
              </div>
              <div className={`flex items-center ${getTrendColor(analytics.trends.meetings_change)}`}>
                {getTrendIcon(analytics.trends.meetings_change)}
                <span className="text-sm">{Math.abs(analytics.trends.meetings_change)}%</span>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-1 text-xs text-slate-500">
              <Calendar className="w-3 h-3" />
              vs previous period
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-sm">Avg Duration</p>
                <p className="text-2xl font-bold text-white">{formatDuration(analytics.summary.avg_duration_minutes)}</p>
              </div>
              <div className={`flex items-center ${getTrendColor(-analytics.trends.duration_change)}`}>
                {getTrendIcon(-analytics.trends.duration_change)}
                <span className="text-sm">{Math.abs(analytics.trends.duration_change)}%</span>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-1 text-xs text-slate-500">
              <Clock className="w-3 h-3" />
              {analytics.summary.total_duration_hours}h total
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-sm">Avg Participants</p>
                <p className="text-2xl font-bold text-white">{analytics.summary.avg_participants}</p>
              </div>
              <div className={`flex items-center ${getTrendColor(analytics.trends.participation_change)}`}>
                {getTrendIcon(analytics.trends.participation_change)}
                <span className="text-sm">{Math.abs(analytics.trends.participation_change)}%</span>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-1 text-xs text-slate-500">
              <Users className="w-3 h-3" />
              {analytics.summary.total_participants} total
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-slate-400 text-sm">Action Completion</p>
                <p className="text-2xl font-bold text-white">
                  {Math.round((analytics.summary.action_items_completed / analytics.summary.action_items_created) * 100)}%
                </p>
              </div>
              <div className={`flex items-center ${getTrendColor(analytics.trends.completion_rate_change)}`}>
                {getTrendIcon(analytics.trends.completion_rate_change)}
                <span className="text-sm">{Math.abs(analytics.trends.completion_rate_change)}%</span>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-1 text-xs text-slate-500">
              <FileText className="w-3 h-3" />
              {analytics.summary.action_items_completed}/{analytics.summary.action_items_created} items
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800 border border-slate-700">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="participation">Participation</TabsTrigger>
          <TabsTrigger value="topics">Topics</TabsTrigger>
          <TabsTrigger value="meetings">Meetings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Meetings by Day */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-teal-400" />
                  Meetings by Day of Week
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analytics.meetings_by_day.map(day => (
                    <div key={day.day} className="flex items-center gap-3">
                      <span className="text-slate-400 text-sm w-24">{day.day}</span>
                      <div className="flex-1">
                        <Progress value={day.percentage} className="h-2" />
                      </div>
                      <span className="text-white text-sm w-8">{day.count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Transcription Quality */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Mic className="w-4 h-4 text-purple-400" />
                  Transcription Quality
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center py-4">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="64" cy="64" r="56"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="12"
                        className="text-slate-700"
                      />
                      <circle
                        cx="64" cy="64" r="56"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="12"
                        strokeDasharray={`${analytics.summary.transcription_accuracy * 3.52} 352`}
                        strokeLinecap="round"
                        className="text-emerald-500"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">{analytics.summary.transcription_accuracy}%</span>
                    </div>
                  </div>
                </div>
                <p className="text-center text-slate-400 text-sm">OpenAI Whisper Accuracy</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="participation" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-400" />
                Speaker Participation by Role
              </CardTitle>
              <CardDescription className="text-slate-400">
                Attendance rate and average speaking time per discipline
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analytics.speaker_participation.map(speaker => (
                  <div key={speaker.role} className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full ${speaker.color}`} />
                    <div className="w-32">
                      <p className="text-white text-sm font-medium">{speaker.role}</p>
                      <p className="text-slate-500 text-xs">{speaker.name}</p>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Progress value={speaker.percentage} className="h-2 flex-1" />
                        <span className="text-slate-400 text-sm w-12">{speaker.percentage}%</span>
                      </div>
                    </div>
                    <div className="text-right w-24">
                      <p className="text-white text-sm">{speaker.avg_speaking_time} min</p>
                      <p className="text-slate-500 text-xs">avg speak time</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="topics" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-amber-400" />
                Common Discussion Topics
              </CardTitle>
              <CardDescription className="text-slate-400">
                Topics identified via AI analysis of meeting transcripts
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {analytics.topics_discussed.map(topic => (
                  <div key={topic.topic} className="p-3 bg-slate-800 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{topic.topic}</span>
                      <div className="flex items-center gap-1">
                        {topic.trend === "up" && <TrendingUp className="w-4 h-4 text-emerald-400" />}
                        {topic.trend === "down" && <TrendingDown className="w-4 h-4 text-red-400" />}
                        {topic.trend === "stable" && <Minus className="w-4 h-4 text-slate-400" />}
                      </div>
                    </div>
                    <Progress value={topic.percentage} className="h-2 mb-1" />
                    <p className="text-slate-400 text-xs">{topic.count} discussions ({topic.percentage}%)</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="meetings" className="mt-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Recent Meetings */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-teal-400" />
                  Recent Meetings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[250px]">
                  <div className="space-y-2">
                    {analytics.recent_meetings.map(meeting => (
                      <div key={meeting.id} className="p-3 bg-slate-800 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium">{meeting.date}</span>
                          <Badge variant="outline" className="text-slate-400 border-slate-600">
                            {formatDuration(meeting.duration)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {meeting.participants}
                          </span>
                          <span className="flex items-center gap-1">
                            <Activity className="w-3 h-3" />
                            {meeting.patients_discussed} patients
                          </span>
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {meeting.action_items} actions
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Most Discussed Patients */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Brain className="w-4 h-4 text-purple-400" />
                  Most Discussed Patients
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[250px]">
                  <div className="space-y-2">
                    {analytics.patients_most_discussed.map((patient, idx) => (
                      <div key={patient.name} className="p-3 bg-slate-800 rounded-lg flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-teal-900 flex items-center justify-center text-teal-400 font-bold">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium">{patient.name}</p>
                          <p className="text-slate-400 text-xs">
                            {patient.discussions} discussions • Last: {patient.last_discussed}
                          </p>
                        </div>
                        <Badge className={patient.status === "Active" ? "bg-emerald-900 text-emerald-300" : "bg-slate-700 text-slate-300"}>
                          {patient.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export { IDGMeetingAnalytics };
